import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { pollingActions } from "modules/polling/slices";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Table } from "components/common/Table";
import {
  TABLE_SORTING_TYPES,
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { format, startOfMonth, sub } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { billingActions } from "modules/billing/slices";
import {
  isUserBillingDataLoadingSelector,
  tableOrganizationsBillingDataSelector,
  tableOrganizationsTotalBillingDataSelector,
  userBillingDataSelector
} from "modules/billing/selectors";
import {
  TableOrganizationsBillingData,
  TableUserOrganizationsBillingData
} from "modules/billing/types";
import { enterprisesActions } from "modules/enterprises/slices";
import { isBillingAdminsListUpdatingSelector } from "modules/enterprises/selectors";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useParams } from "react-router-dom";
import { downloadFile } from "utils/downloadFile";
import { API_PATH } from "../../axios";
import {
  DATE_FORMATS,
  DEFAULT_CURRENCY,
  ERROR_MESSAGES,
  REGEX,
  ROUTES
} from "../../constants";
import * as s from "./styles";
import { DATE_TYPES, DIALOG_TYPES } from "./types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { userSelector } from "modules/auth/selectors";
import { Button } from "@mui/material";
import { array, string } from "yup";

const POLL_ID_PREFIX = "USER_BILLING";
const POLL_IDS = {
  userBillingData: "USER_BILLING"
};

const CURRENCY_SELECT_ID = "currency";

const title = "Billing";

export const BillingOrganizations: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    userId: string;
  }>();
  const user = useSelector(userSelector);

  const billingData = useSelector(userBillingDataSelector);
  const isUserBillingDataLoading = useSelector(
    isUserBillingDataLoadingSelector
  );
  const tableBillingData = useSelector(tableOrganizationsBillingDataSelector);
  const tableBillingDataTotal = useSelector(
    tableOrganizationsTotalBillingDataSelector
  );

  const isBillingAdminsListUpdating = useSelector(
    isBillingAdminsListUpdatingSelector
  );
  const isOperationInProgress = isBillingAdminsListUpdating;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);

  // to hide admins column if it is empty for all orgs
  const allAdminsEmpty = useMemo(
    () =>
      billingData?.organizations?.every(
        (org) => org.billing_admins.length === 0
      ) ?? true,
    [billingData]
  );

  const billingTableColumns: TableColumn<TableUserOrganizationsBillingData>[] =
    [
      { key: "name", label: "Organization Name" },
      { key: "status", label: "Status" },
      { key: "billing_role", label: "User Role" },
      ...(allAdminsEmpty
        ? []
        : [
            { key: "billing_admins_str", label: "Billing Admins", width: "28%" }
          ])
    ];

  const currentDate = useMemo(() => new Date(), []);
  const [minDate] = useState<Dayjs>(() =>
    dayjs(sub(currentDate, { days: 120 })).startOf("day")
  );

  const [startDate, setStartDate] = useState<Dayjs | null>(() =>
    dayjs(startOfMonth(currentDate)).startOf("day")
  );

  const [endDate, setEndDate] = useState<Dayjs | null>(() =>
    dayjs(currentDate).startOf("day")
  );

  const startDateStr = startDate?.format(DATE_FORMATS.DAYJS_ISO_DATE) ?? null;
  const endDateStr = endDate?.format(DATE_FORMATS.DAYJS_ISO_DATE) ?? null;

  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);

  const currencies = useMemo(
    () => (billingData ? Object.keys(billingData.costs) : ["chf", "eur"]),
    [billingData]
  );

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.GENERATE_ORGS_BILLING_REPORT, isOpened: false });

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
  }, [dialog]);

  const handleGenerateOrgsBillingReportButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.GENERATE_ORGS_BILLING_REPORT,
      isOpened: true
    });
  }, []);

  const handleUpdateBillingAdminsListMenuItemClick = useCallback(
    (id: string) => {
      setSelectedItemId(id);
      setDialog({
        type: DIALOG_TYPES.UPDATE_BILLING_ADMINS_LIST,
        isOpened: true
      });
    },
    []
  );

  const breadcrumbs: Breadcrumb[] = [
    { text: "Billing", url: ROUTES.BILLING_ORGANIZATIONS }
  ];

  const tableActions: TableRowActionsMenuItem<TableOrganizationsBillingData>[] =
    [
      {
        label: "Update billing admins list",
        handler: handleUpdateBillingAdminsListMenuItemClick,
        isDisabled: (org) =>
          org.status !== "Active" || org.billing_role !== "Owner"
      }
    ];

  const generateTableItemURL = useCallback(
    (id: string) =>
      generatePath(ROUTES.BILLING_ORGANIZATION, {
        organizationId: id
      }),
    []
  );
  const handleConfirmDownloadButtonClick = useCallback(
    (data: { start_date: string; end_date: string }) => {
      const startDate = format(
        new Date(data.start_date),
        DATE_FORMATS.ISO_DATE
      );
      const endDate = format(new Date(data.end_date), DATE_FORMATS.ISO_DATE);
      downloadFile(
        `${API_PATH}gotham-wayne-tower/method/billing/users/${user?.id}/download-report?start=${startDate}&end=${endDate}`
      );
      dispatch(
        notificationsActions.showNotification({
          title: "Billing report is generating. Download will start soon.",
          type: NOTIFICATION_TYPES.INFO
        })
      );
      handleCloseDialog();
    },
    [user?.id, dispatch, handleCloseDialog]
  );

  const handleConfirmUpdateBillingAdminsList = useCallback(
    (data: { admins: string[] }) => {
      if (selectedItemId) {
        dispatch(
          enterprisesActions.updateBillingAdminsListRequest({
            orgId: selectedItemId,
            admins: data.admins
          })
        );
      }
      handleCloseDialog();
    },
    [dispatch, selectedItemId, handleCloseDialog]
  );

  useMount(() => {
    if (user?.id) {
      if (startDateStr && endDateStr) {
        dispatch(
          billingActions.getUserBillingDataRequest({
            startDate: startDateStr,
            endDate: endDateStr,
            userId: user.id
          })
        );
        // dispatch(
        //   pollingActions.startPolling({
        //     id: `${POLL_ID_PREFIX}/${POLL_IDS.userBillingData}`,
        //     action: billingActions.getUserBillingDataRequest({
        //       startDate: startDateStr,
        //       endDate: endDateStr,
        //       userId: user.id
        //     })
        //   })
        // );
      }
    }
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(billingActions.clear());
  });

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      if (user?.id) {
        if (startDateStr && endDateStr) {
          dispatch(
            billingActions.getUserBillingDataRequest({
              startDate: startDateStr,
              endDate: endDateStr,
              userId: user.id
            })
          );
        }
      }
    }
  }, [
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    user?.id,
    startDateStr,
    endDateStr
  ]);

  useEffect(() => {
    if (user?.id) {
      if (startDateStr && endDateStr) {
        dispatch(
          billingActions.getUserBillingDataRequest({
            startDate: startDateStr,
            endDate: endDateStr,
            userId: user.id
          })
        );
      }
    }
  }, [dispatch, startDateStr, endDateStr, matchParams.userId, user?.id]);

  const handleDateChange = useCallback(
    (dateType: DATE_TYPES, value: Dayjs | null) => {
      switch (dateType) {
        case DATE_TYPES.START:
          setStartDate(value);
          break;
        case DATE_TYPES.END:
          setEndDate(value);
          break;
      }
    },
    []
  );

  const handleCurrencyChange = useCallback((event: SelectChangeEvent) => {
    setCurrency(event.target.value);
  }, []);

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.GENERATE_ORGS_BILLING_REPORT]: {
      title: `Generate billing report`,
      onConfirm: handleConfirmDownloadButtonClick,
      confirmButtonLabel: "Download",
      fields: [
        {
          name: "start_date",
          type: FIELD_TYPES.DATE_PICKER,
          label: "Start-date",
          minDate: minDate,
          disableFuture: true,
          defaultValue: startDate
        },
        {
          name: "end_date",
          type: FIELD_TYPES.DATE_PICKER,
          label: "End-date",
          disableFuture: true,
          defaultValue: endDate
        },
        {
          name: "notes",
          type: FIELD_TYPES.LABEL,
          label: `ℹ️ Billing data is available for previous 120 days`
        },
        {
          name: "note2s",
          type: FIELD_TYPES.LABEL,
          label: `ℹ️ The report includes only organizations owned by the current user.`
        }
      ]
    },
    [DIALOG_TYPES.UPDATE_BILLING_ADMINS_LIST]: {
      title: `Update billing administrators`,
      onConfirm: handleConfirmUpdateBillingAdminsList,
      confirmButtonLabel: "Update",
      fields: [
        {
          name: "admins",
          type: FIELD_TYPES.MULTISELECT,
          label: "E-mails of billing administrators",
          options: [],
          freeSolo: true,
          defaultValue:
            billingData?.organizations?.find((org) => org.id === selectedItemId)
              ?.billing_admins ?? [],
          rules: array()
            .of(
              string().matches(
                REGEX.EMAIL_ADDRESS,
                ERROR_MESSAGES.EMAIL_ADDRESS
              )
            )
            .max(5, "You can add up to 5 billing administrators")
            .default([]),
          helperText: 'Press "Enter" to add a new admin'
        },
        {
          name: "info",
          type: FIELD_TYPES.LABEL,
          label: `\nℹ️ Only already registered users can be assigned as billing administrators.\n\n`
        }
      ]
    }
  };

  return (
    <>
      <Head title={title} />
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <s.Title variant={"h4"} component={"h2"}>
        {title}
      </s.Title>
      <s.ProjectsContainer>
        <s.Description>
          ℹ️ This section displays only organizations where the user has the
          owner or billing-administrator role.
        </s.Description>
        {/* <s.Description>
          ℹ️ This billing data is only for the initial reference. The final
          costs will be provided in the invoice.
        </s.Description> */}
        <s.Description2>
          ℹ️ Billing data is available for the last 120 days.
        </s.Description2>
        <Table
          isSearchEnabled={true}
          actions={tableActions}
          isSortingEnabled={true}
          rows={tableBillingData || []}
          // rows={tableBillingData?.filter((pr) => pr.status === "Active") || []}
          // disabledRows={
          //   tableBillingData?.filter((pr) => pr.status === "Deleted") || []
          // }
          columns={[
            ...billingTableColumns,
            {
              key: `cost-${currency}`,
              label: "Cost",
              sortingType: TABLE_SORTING_TYPES.NUMBER
            }
          ]}
          totalRow={tableBillingDataTotal || undefined}
          itemLink={{
            column: "name",
            getURL: generateTableItemURL
            // isEnabled: (billingData) =>
            //   billingData.status !== ORG_STATUSES_LABELS[ORG_STATUSES.DELETED]
          }}
          isLoading={isUserBillingDataLoading}
          toolbarItems={
            <>
              <span>
                <Button
                  onClick={handleGenerateOrgsBillingReportButtonClick}
                  disabled={!billingData}
                  variant="contained"
                  sx={{ marginRight: "10px" }}
                >
                  Generate billing report
                </Button>
              </span>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  name={"start"}
                  label={"Start date"}
                  onChange={(value) =>
                    handleDateChange(DATE_TYPES.START, value as Dayjs | null)
                  }
                  value={startDate || null}
                  format={"DD MMM YYYY"}
                  minDate={minDate}
                  maxDate={endDate || undefined}
                  disableFuture={true}
                  sx={{ marginRight: "10px", maxWidth: "170px" }}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  name={"end"}
                  label={"End date"}
                  onChange={(value) =>
                    handleDateChange(DATE_TYPES.END, value as Dayjs | null)
                  }
                  value={endDate || null}
                  format={"DD MMM YYYY"}
                  minDate={startDate || minDate}
                  disableFuture={true}
                  sx={{
                    marginRight: "10px",
                    maxWidth: "170px",
                    fontSize: "4px"
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </LocalizationProvider>
              {currencies.length > 0 && (
                <s.CurrencyFormControl>
                  <InputLabel id={CURRENCY_SELECT_ID}>Currency</InputLabel>
                  <Select
                    label={"Currency"}
                    labelId={CURRENCY_SELECT_ID}
                    value={currency}
                    onChange={handleCurrencyChange}
                    sx={{ marginRight: "10px", maxWidth: "100px" }}
                    size={"small"}
                  >
                    {currencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </s.CurrencyFormControl>
              )}
            </>
          }
        />
        <FormDialog
          isOpened={dialog.isOpened}
          onCancel={handleCloseDialog}
          fields={dialogProps[dialog.type].fields}
          onConfirm={dialogProps[dialog.type].onConfirm}
          title={dialogProps[dialog.type].title}
          confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
        />
      </s.ProjectsContainer>
    </>
  );
};
